import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Chip,
} from '@mui/material';
import {
    Inventory as InventoryIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const InventorySetupDialog = ({ open, product, onClose }) => {
    const navigate = useNavigate();

    const handleSetupInventoryNow = () => {
        onClose();
        navigate('/app/inventory', {
            state: {
                newProductId: product?.id,
                showAddInventory: true
            }
        });
    };

    const handleSetupLater = () => {
        onClose();
    };

    if (!product) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="h6" component="span">
                        Product Created Successfully
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {product.name}
                        </Typography>
                        <Chip
                            label={product.category_name}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        SKU: {product.sku}
                    </Typography>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                    Would you like to set up inventory levels for your branches now?
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box
                        onClick={handleSetupInventoryNow}
                        sx={{
                            p: 2,
                            border: 2,
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            cursor: 'pointer',
                            bgcolor: 'primary.50',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: 'primary.100',
                            }
                        }}
                    >
                        <InventoryIcon color="primary" />
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Set Up Inventory Now
                                </Typography>
                                <Chip label="Recommended" size="small" color="primary" />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Configure stock levels, costs, and pricing for each branch
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        onClick={handleSetupLater}
                        sx={{
                            p: 2,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: 'grey.50',
                            }
                        }}
                    >
                        <ScheduleIcon color="action" />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                I'll Do This Later
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                You can add inventory through the Inventory Management section
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Alert severity="info" sx={{ mt: 3 }} icon={false}>
                    <Typography variant="body2">
                        <strong>Note:</strong> Products without inventory won't appear in sales until stock is added to at least one branch.
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleSetupLater}>
                    Skip for Now
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSetupInventoryNow}
                    startIcon={<InventoryIcon />}
                >
                    Set Up Inventory
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InventorySetupDialog;
